// Career progress: per level best score and stars, licence endorsements, XP. Stored locally and – when logged
// in on atc.coone.dev – in the user's server profile. The legacy per-position XP fields of the previous game
// (DEL, APN, GND, TWR, DEP, APP, CTR) are kept up to date so the existing leaderboard keeps working.
import { LEVELS, levelById, type LevelDef } from './levels';
import type { ScoreSummary } from '../sim/scoring';

export interface LevelProgress { best: number; stars: number; passed: boolean; plays: number; last: string }
export interface Career {
  v: 2;
  levels: Record<string, LevelProgress>;
  endorsements: string[];
  xp: number;
}

export const emptyCareer = (): Career => ({ v: 2, levels: {}, endorsements: [], xp: 0 });

export function isUnlocked(c: Career, lv: LevelDef): boolean {
  return lv.requires.every(r => c.levels[r]?.passed);
}
export function stars(score: number, pass: number): number {
  if (score < pass) return 0;
  return score >= 92 ? 3 : score >= (pass + 92) / 2 ? 2 : 1;
}
export function xpFor(lv: LevelDef, score: number): number {
  const weight = 1 + lv.roles.length * 0.5 + (lv.stage === 'CHECKOUT' ? 1 : 0);
  return Math.round(score * weight * (lv.minutes / 20));
}

export interface ResultApplied { career: Career; passed: boolean; newStars: number; xpGained: number; newEndorsement: string | null; unlocked: LevelDef[] }

export function applyResult(c0: Career, lv: LevelDef, s: ScoreSummary): ResultApplied {
  const c: Career = structuredClone(c0);
  const prev = c.levels[lv.id];
  const passed = !s.relieved && s.total >= lv.passScore;
  const st = s.relieved ? 0 : stars(s.total, lv.passScore);
  const unlockedBefore = new Set(LEVELS.filter(l => isUnlocked(c0, l)).map(l => l.id));
  const gainedScore = Math.max(0, s.total - (prev?.best ?? 0));
  const xpGained = Math.round(xpFor(lv, gainedScore) + (passed ? 10 : 2));
  c.levels[lv.id] = { best: Math.max(prev?.best ?? 0, s.total), stars: Math.max(prev?.stars ?? 0, st), passed: !!prev?.passed || passed, plays: (prev?.plays ?? 0) + 1, last: new Date().toISOString() };
  c.xp += xpGained;
  let newEndorsement: string | null = null;
  if (passed && lv.endorsement && !c.endorsements.includes(lv.endorsement)) { c.endorsements.push(lv.endorsement); newEndorsement = lv.endorsement; }
  const unlocked = LEVELS.filter(l => isUnlocked(c, l) && !unlockedBefore.has(l.id));
  return { career: c, passed, newStars: st, xpGained, newEndorsement, unlocked };
}

/** Merge two careers (local and server) – best of both. */
export function mergeCareers(a: Career, b: Career): Career {
  const c = emptyCareer();
  for (const id of new Set([...Object.keys(a.levels), ...Object.keys(b.levels)])) {
    const x = a.levels[id], y = b.levels[id];
    if (!x || !y) { c.levels[id] = (x ?? y)!; continue; }
    c.levels[id] = { best: Math.max(x.best, y.best), stars: Math.max(x.stars, y.stars), passed: x.passed || y.passed, plays: Math.max(x.plays, y.plays), last: x.last > y.last ? x.last : y.last };
  }
  c.endorsements = [...new Set([...a.endorsements, ...b.endorsements])];
  c.xp = Math.max(a.xp, b.xp);
  return c;
}

const KEY = 'frequenz_career';
export function loadLocal(): Career {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) { const c = JSON.parse(raw) as Career; if (c.v === 2) return c; }
  } catch { /* storage unavailable */ }
  return emptyCareer();
}
export function saveLocal(c: Career): void {
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch { /* storage unavailable */ }
}

/** Server progress object: the career plus legacy per-position XP for the existing leaderboard. */
export function toServerProgress(c: Career, previous: Record<string, unknown> | undefined): Record<string, unknown> {
  const legacy: Record<string, { xp: number; tutorial: boolean; shifts: number; handled: number; incidents: number; best: number }> = {};
  const pos = (role: string): string => (role === 'DIR' ? 'APP' : role);
  for (const [id, p] of Object.entries(c.levels)) {
    const lv = levelById(id);
    if (!lv) continue;
    const share = xpFor(lv, p.best) / lv.roles.length;
    for (const r of lv.roles) {
      const k = pos(r);
      const e = legacy[k] ?? { xp: 0, tutorial: true, shifts: 0, handled: 0, incidents: 0, best: 0 };
      e.xp += Math.round(share); e.shifts += p.plays; e.best = Math.max(e.best, p.best);
      legacy[k] = e;
    }
  }
  // never lower XP the player earned in the previous game
  for (const [k, v] of Object.entries(previous ?? {})) {
    if (k === 'career' || !v || typeof v !== 'object') continue;
    const old = v as { xp?: number };
    const cur = legacy[k];
    if (!cur) legacy[k] = v as never;
    else cur.xp = Math.max(cur.xp, Number(old.xp) || 0);
  }
  return { ...(previous ?? {}), ...legacy, career: c };
}
export function fromServerProgress(p: Record<string, unknown> | undefined): Career | null {
  const c = p?.career as Career | undefined;
  return c && c.v === 2 ? c : null;
}
